import { Button, ButtonColor, ImageUploader, ImageUploaderType, Input } from "@swivl-mono/ui";
import { useNavigate, useParams } from "react-router-dom";
import { shallow } from "zustand/shallow";

import BrandsModel from "../../../Models/Brands/Brands.Model";
import { BrandColorPanel } from "./Components/BrandColorPanel";
import { AlertsModel } from "../../../Models/Notifications/Alerts.Model";
import { useEffect, useState } from "react";

export function BrandDetailsView() {
  const { brands } = BrandsModel.useState((state) => state, shallow);
  const brandId = useParams().brandId;
  const brand = brands && brandId ? brands[parseInt(brandId)] : undefined;
  const [brandUrls, setBrandUrls] = useState({
    spotSlug: brand?.spotSlug ?? "",
    spotLinkSlug: brand?.spotLinkSlug ?? "",
  });

  // update brandurls when brand changes
  useEffect(() => {
    if (brand) {
      setBrandUrls({
        spotSlug: brand.spotSlug,
        spotLinkSlug: brand.spotLinkSlug,
      });
    }
  }, [brand]);

  const navigate = useNavigate();
  if (!brand) {
    return <div>Brand not found</div>;
  }

  const handleSetBrandUrls = (key: string, value: string) => {
    setBrandUrls((prev) => ({
      ...prev,
      [key]: value,
    }));
  }

  const changeKey = async (key: string, value: any) => {
    if (!value) return;

    if (key === "spotSlug") {
      const isBrandSlugUnique = await BrandsModel.getIsBrandSlugUnique(Number(brandId), value)

      if (isBrandSlugUnique) {
        let b = { ...brand };
        (b as any)[key] = value;
        BrandsModel.updateBrand(b);
      } else {
        AlertsModel.errorToast("Spot URL already exists");
        return;
      }
    } else if (key === "spotLinkSlug") {
      const isBrandSlugUnique = await BrandsModel.getIsBrandSlugUnique(Number(brandId), undefined, value)
      if (isBrandSlugUnique) {
        let b = { ...brand };
        (b as any)[key] = value;
        BrandsModel.updateBrand(b);
      }
      else {
        AlertsModel.errorToast("Spot Short Link URL already exists");
        return;
      }
    } else {
      let b = { ...brand };
      (b as any)[key] = value;
      BrandsModel.updateBrand(b);
    };
  }

  const onColorChange = (name: string, color?: string) => {
    let b = { ...brand };
    (b.theme as any)[name] = color;
    BrandsModel.updateBrand(b);
  };

  return (
    <div className="p-5">
      <h5 className="mb-5">
        {" "}
        Brand Settings
        {!brand.isDefault && (
          <Button
            className="ml-auto"
            color={ButtonColor.WHITE}
            onClickAsync={async () => {
              if (brand.isDefault) {
                alert("Cannot delete default brand");
                return;
              }
              if (window.confirm("Are you sure you want to delete this brand?")) {
                await BrandsModel.deleteBrand(brand.id);
                navigate("/properties/page/brands");
              }
            }}
          >
            Delete Brand
          </Button>
        )}
      </h5>
      <div className="mb-5">
        <label>Brand Avatar</label>
        <div className="avatar">
          <ImageUploader
            isEditable={true}
            text="Click to Change"
            type={ImageUploaderType.LOCATION}
            imageURL={brand.avatarURL || "https://swivl-cdn.s3-us-west-1.amazonaws.com/avatar-default-org.png"}
            imageUploaded={(url) => {
              changeKey("avatarURL", url);
            }}
          />
        </div>
      </div>
      <div className="mb-5">
        <label>Brand Name</label>
        <Input
          name="brandName"
          value={brand.name}
          onChange={(e) => {
            changeKey("name", e.target.value);
          }}
        />
      </div>

      <div className="mb-5">
        <label>Spot URL (https://my.spot.is/{brand.spotSlug})</label>
        <Input
          name="spotSlug"
          value={brandUrls.spotSlug}
          onBlur={(e) => {
            changeKey("spotSlug", e.target.value);
          }}
          onChange={(e) => {
            handleSetBrandUrls("spotSlug", e.target.value);
          }}
        />
      </div>
      <div className="mb-5">
        <label htmlFor="spotLinkSlug">Spot Short Link URL (https://my.spot.is/{brand.spotLinkSlug})</label>
        <Input
          id="spotLinkSlug"
          name="spotLinkSlug"
          value={brandUrls.spotLinkSlug}
          onBlur={(e) => {
            changeKey("spotLinkSlug", e.target.value);
          }}
          onChange={(e) => {
            handleSetBrandUrls("spotLinkSlug", e.target.value);
          }}
        />
      </div>

      <label>Colors</label>
      <BrandColorPanel
        brand={brand}
        onColorChange={onColorChange}
        label={"Primary Color"}
        colors={[
          { label: "Background", key: "primaryBackground" },
          { label: "Text", key: "primaryText" },
          { label: "Hover", key: "primaryHover" },
          { label: "Hover Text", key: "primaryHoverText" },
          { label: "Active", key: "primaryActive" },
          { label: "Active Text", key: "primaryActiveText" },
        ]}
      />

      <BrandColorPanel
        brand={brand}
        onColorChange={onColorChange}
        label={"Secondary Color"}
        colors={[
          { label: "Background", key: "secondaryBackground" },
          { label: "Text", key: "secondaryText" },
          { label: "Hover", key: "secondaryHover" },
          { label: "Hover Text", key: "secondaryHoverText" },
          { label: "Active", key: "secondaryActive" },
          { label: "Active Text", key: "secondaryActiveText" },
        ]}
      />

      <BrandColorPanel
        brand={brand}
        onColorChange={onColorChange}
        label={"Typography"}
        colors={[
          { label: "Text", key: "text" },
          { label: "Text Background", key: "textBackground" },
          { label: "Chat: User Bubble", key: "userBubbleBackground" },
          { label: "Chat: User Bubble Text", key: "userBubbleText" },
          { label: "Chat: Bot Bubble", key: "botBubbleBackground" },
          { label: "Chat: Bot Bubble Text", key: "botBubbleText" },
        ]}
      />
    </div>
  );
}
